import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../lib/api';

interface Stats {
  users: number;
  posts: number;
  pendingPosts: number;
  openReports: number;
  activeAds: number;
}

export default function AdminDashboard() {
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const loadStats = async () => {
      try {
        const res = await api.admin.getStats();
        setStats(res);
      } catch (err) {
        console.error('Failed to load stats:', err);
      } finally {
        setLoading(false);
      }
    };
    
    loadStats();
  }, []);
  
  if (loading) {
    return (
      <div className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="card p-6 animate-pulse">
              <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-20 mb-2" />
              <div className="h-8 bg-slate-200 dark:bg-slate-700 rounded w-16" />
            </div>
          ))}
        </div>
      </div>
    );
  }
  
  return (
    <div className="p-6 space-y-6">
      <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Dashboard</h1>
      
      {/* Quick stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Users"
          value={stats?.users || 0}
          icon={<UsersIcon className="w-6 h-6" />}
          color="blue"
        />
        <StatCard
          title="Total Posts"
          value={stats?.posts || 0}
          icon={<PostsIcon className="w-6 h-6" />}
          color="green"
        />
        <StatCard
          title="Pending Reports"
          value={stats?.openReports || 0}
          link="/lockhouse/reports"
          icon={<ReportsIcon className="w-6 h-6" />}
          color="orange"
          urgent={Boolean(stats?.openReports && stats.openReports > 0)}
        />
        <StatCard
          title="Moderation Queue"
          value={stats?.pendingPosts || 0}
          link="/lockhouse/moderation"
          icon={<ModerationIcon className="w-6 h-6" />}
          color="purple"
          urgent={Boolean(stats?.pendingPosts && stats.pendingPosts > 0)}
        />
      </div>
      
      {/* Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card p-6">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">Platform Stats</h2>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-slate-600 dark:text-slate-400">Active Ads</span>
              <span className="font-semibold text-slate-900 dark:text-white">
                {stats?.activeAds || 0}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-slate-600 dark:text-slate-400">Public Posts</span>
              <span className="font-semibold text-slate-900 dark:text-white">
                {(stats?.posts || 0) - (stats?.pendingPosts || 0)}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-slate-600 dark:text-slate-400">Registered Users</span>
              <span className="font-semibold text-slate-900 dark:text-white">
                {stats?.users || 0}
              </span>
            </div>
          </div>
        </div>
        
        <div className="card p-6">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">Quick Actions</h2>
          <div className="grid grid-cols-2 gap-3">
            <Link
              to="/lockhouse/moderation"
              className="p-4 bg-slate-50 dark:bg-slate-700/50 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors text-center"
            >
              <ModerationIcon className="w-8 h-8 text-purple-500 mx-auto mb-2" />
              <span className="text-sm font-medium text-slate-900 dark:text-white">Review Content</span>
            </Link>
            <Link
              to="/lockhouse/reports"
              className="p-4 bg-slate-50 dark:bg-slate-700/50 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors text-center"
            >
              <ReportsIcon className="w-8 h-8 text-orange-500 mx-auto mb-2" />
              <span className="text-sm font-medium text-slate-900 dark:text-white">View Reports</span>
            </Link>
            <Link
              to="/lockhouse/users"
              className="p-4 bg-slate-50 dark:bg-slate-700/50 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors text-center"
            >
              <UsersIcon className="w-8 h-8 text-blue-500 mx-auto mb-2" />
              <span className="text-sm font-medium text-slate-900 dark:text-white">Manage Users</span>
            </Link>
            <Link
              to="/lockhouse/ads"
              className="p-4 bg-slate-50 dark:bg-slate-700/50 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors text-center"
            >
              <AdsIcon className="w-8 h-8 text-green-500 mx-auto mb-2" />
              <span className="text-sm font-medium text-slate-900 dark:text-white">Manage Ads</span>
            </Link>
          </div>
        </div>
      </div>
      
      {/* Recent activity */}
      <div className="card p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Recent Activity</h2>
          <Link to="/lockhouse/audit" className="text-sm text-primary-500 hover:text-primary-600">
            View all
          </Link>
        </div>
        <div className="space-y-3">
          <ActivityItem
            action="User banned"
            target="@spammer123"
            actor="admin"
            time="5 minutes ago"
            type="danger"
          />
          <ActivityItem
            action="Post removed"
            target="Post #4521"
            actor="admin"
            time="15 minutes ago"
            type="warning"
          />
          <ActivityItem
            action="Report resolved"
            target="Report #892"
            actor="admin"
            time="1 hour ago"
            type="success"
          />
          <ActivityItem
            action="New user registered"
            target="@newboater"
            time="2 hours ago"
            type="info"
          />
        </div>
      </div>
    </div>
  );
}

function StatCard({
  title,
  value,
  change,
  link,
  icon,
  color,
  urgent,
}: {
  title: string;
  value: number;
  change?: string;
  link?: string;
  icon: React.ReactNode;
  color: 'blue' | 'green' | 'orange' | 'purple';
  urgent?: boolean;
}) {
  const colorClasses = {
    blue: 'bg-blue-50 dark:bg-blue-900/30 text-blue-500',
    green: 'bg-green-50 dark:bg-green-900/30 text-green-500',
    orange: 'bg-orange-50 dark:bg-orange-900/30 text-orange-500',
    purple: 'bg-purple-50 dark:bg-purple-900/30 text-purple-500',
  };
  
  const content = (
    <>
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm text-slate-600 dark:text-slate-400">{title}</span>
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${colorClasses[color]}`}>
          {icon}
        </div>
      </div>
      <p className={`text-3xl font-bold ${urgent ? 'text-red-500' : 'text-slate-900 dark:text-white'}`}>
        {value.toLocaleString()}
      </p>
      {change && <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">{change}</p>}
    </>
  );
  
  if (link) {
    return (
      <Link to={link} className="card p-6 hover:shadow-md transition-shadow">
        {content}
      </Link>
    );
  }
  
  return <div className="card p-6">{content}</div>;
}

function ActivityItem({
  action,
  target,
  actor,
  time,
  type,
}: {
  action: string;
  target: string;
  actor?: string;
  time: string;
  type: 'success' | 'warning' | 'danger' | 'info';
}) {
  const typeColors = {
    success: 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400',
    warning: 'bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400',
    danger: 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400',
    info: 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400',
  };
  
  return (
    <div className="flex items-center gap-3 p-3 rounded-lg bg-slate-50 dark:bg-slate-700/30">
      <div className={`w-2 h-2 rounded-full ${typeColors[type].split(' ')[0]}`} />
      <div className="flex-1">
        <p className="text-sm text-slate-900 dark:text-white">
          <span className="font-medium">{action}</span>
          <span className="text-slate-500 dark:text-slate-400"> · {target}</span>
        </p>
        <p className="text-xs text-slate-400">
          {actor && <span>by {actor} · </span>}
          {time}
        </p>
      </div>
    </div>
  );
}

// Icons
function UsersIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
    </svg>
  );
}

function PostsIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
    </svg>
  );
}

function ReportsIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
    </svg>
  );
}

function ModerationIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
    </svg>
  );
}

function AdsIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z" />
    </svg>
  );
}
